import { API_URL, API_VERSION } from '../config/api';
import { toast } from 'sonner';
import { getVersionHeaders, checkVersionCompatibility } from '../utils/versionCheck';

// Helper function to get auth headers
export const getAuthHeaders = () => {
  const user = JSON.parse(localStorage.getItem('qualiz_user') || '{}');
  const token = user.token;

  const headers = {
    'Content-Type': 'application/json',
    ...getVersionHeaders(), // Add version headers
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Helper function to handle API responses
const handleResponse = async (response, options = {}) => {
  // Check for version compatibility warnings
  checkVersionCompatibility(response);

  let data;
  try {
    data = await response.json();
  } catch (parseError) {
    const text = await response.text().catch(() => '');
    data = text ? { message: text } : null;
  }

  if (!response.ok) {
    const errorMessage = (data && data.message) || 'API request failed';

    // Show error toast automatically (unless suppressed)
    if (!options.suppressToast) {
      toast.error(errorMessage, { duration: 4500 });
    }

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      // Check if this is due to deployment/version mismatch
      const serverVersion = response.headers.get('X-App-Version');
      const isDeployment =
        serverVersion && sessionStorage.getItem('last_server_version') !== serverVersion;

      if (isDeployment) {
        sessionStorage.setItem('last_server_version', serverVersion);
        toast.info('New version deployed. Please sign in again.', {
          duration: 6000,
          description: 'The application has been updated with new features.',
        });
      } else {
        toast.error('Your session has expired. Please sign in again.', {
          duration: 5000,
        });
      }

      // Store redirect URL to return user after re-login
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/' && currentPath !== '/login') {
        sessionStorage.setItem('redirect_after_login', currentPath);
      }

      localStorage.removeItem('qualiz_user');
      localStorage.removeItem('user');

      // Delay redirect slightly so toast is visible
      setTimeout(() => {
        window.location.href = '/';
      }, 500);

      throw new Error((data && data.message) || 'Authentication failed');
    }

    // Still throw error for component-level error handling
    throw new Error(errorMessage);
  }

  return data;
};

export const api = {
  // Authentication
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return handleResponse(response);
  },

  generateMessage: async ({ prompt, model }) => {
    const response = await fetch(`${API_URL}/ai/messaging/generate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ prompt, model }),
    });
    return handleResponse(response);
  },

  register: async userData => {
    const response = await fetch(`${API_URL}/users/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  logout: async () => {
    const response = await fetch(`${API_URL}/users/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getCurrentUser: async () => {
    const response = await fetch(`${API_URL}/users/me`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateOnboardingTourStatus: async completed => {
    const response = await fetch(`${API_URL}/users/me/onboarding-tour`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ completed }),
    });
    return handleResponse(response);
  },

  getPublicSystemSettings: async () => {
    const response = await fetch(`${API_URL}/system/public-settings`);
    return handleResponse(response);
  },
  // Users
  getUsers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/users${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  createUser: async userData => {
    const response = await fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  adminCreateUser: async userData => {
    const response = await fetch(`${API_URL}/users/admin/create`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  getUserById: async id => {
    const response = await fetch(`${API_URL}/users/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getUsersByRole: async role => {
    const response = await fetch(`${API_URL}/users/role/${role}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateUser: async (id, userData) => {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  adminUpdateUser: async (id, userData) => {
    const response = await fetch(`${API_URL}/users/admin/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  adminDeleteUser: async id => {
    const response = await fetch(`${API_URL}/users/admin/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Quizzes
  createQuiz: async quizData => {
    const response = await fetch(`${API_URL}/quizzes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(quizData),
    });
    return handleResponse(response);
  },

  getQuizzes: async () => {
    const response = await fetch(`${API_URL}/quizzes`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getQuiz: async id => {
    const response = await fetch(`${API_URL}/quizzes/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateQuiz: async (id, quizData) => {
    const response = await fetch(`${API_URL}/quizzes/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(quizData),
    });
    return handleResponse(response);
  },

  updateQuizLearningResource: async (quizId, learningResource) => {
    const response = await fetch(`${API_URL}/quizzes/${quizId}/learning-resource`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ learningResource }),
    });
    return handleResponse(response);
  },

  // Generate questions from uploaded file
  generateQuestionsFromFile: async (file, options = {}) => {
    const {
      questionCount = 10,
      difficulty = 'medium',
      selectedChunks = null,
      generateExplanations = true,
    } = options;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('questionCount', questionCount.toString());
    formData.append('difficulty', difficulty);
    formData.append('generateExplanations', generateExplanations.toString());

    if (selectedChunks && Array.isArray(selectedChunks)) {
      formData.append('selectedChunks', JSON.stringify(selectedChunks));
    }

    // Get auth token
    const user = JSON.parse(localStorage.getItem('qualiz_user') || '{}');
    const token = user.token;

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Note: Don't set Content-Type header for FormData - browser will set it with boundary

    const response = await fetch(`${API_URL}/quizzes/generate-from-file`, {
      method: 'POST',
      headers: headers,
      body: formData,
    });

    return handleResponse(response);
  },

  // Generate questions from webpage URL
  generateQuestionsFromUrl: async (url, options = {}) => {
    const { questionCount = 10, difficulty = 'medium', generateExplanations = true } = options;

    const response = await fetch(`${API_URL}/quizzes/generate-from-url`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        url,
        questionCount,
        difficulty,
        generateExplanations,
      }),
    });

    return handleResponse(response);
  },

  // Generate questions from YouTube video
  generateQuestionsFromYouTube: async (options = {}) => {
    const {
      youtubeUrl,
      questionCount = 10,
      difficulty = 'medium',
      language = 'en',
      generateExplanations = true,
    } = options;

    const response = await fetch(`${API_URL}/quizzes/generate-from-youtube`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        youtubeUrl,
        questionCount,
        difficulty,
        language,
        generateExplanations,
      }),
    });

    return handleResponse(response);
  },

  // Quiz Sessions
  createQuizSession: async sessionData => {
    const response = await fetch(`${API_URL}/quiz-sessions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(sessionData),
    });
    return handleResponse(response);
  },

  getStudentSessions: async studentEmail => {
    const response = await fetch(`${API_URL}/quiz-sessions/student/${studentEmail}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Goals
  createGoal: async goalData => {
    const response = await fetch(`${API_URL}/goals`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(goalData),
    });
    return handleResponse(response);
  },

  getStudentGoals: async studentId => {
    const response = await fetch(`${API_URL}/goals/student/${studentId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateGoal: async (goalId, goalData) => {
    const response = await fetch(`${API_URL}/goals/${goalId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(goalData),
    });
    return handleResponse(response);
  },

  deleteGoal: async goalId => {
    const response = await fetch(`${API_URL}/goals/${goalId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Resources
  createResource: async resourceData => {
    const response = await fetch(`${API_URL}/resources`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(resourceData),
    });
    return handleResponse(response);
  },

  getResources: async () => {
    const response = await fetch(`${API_URL}/resources`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getAllQuizzesSummary: async () => {
    const response = await fetch(`${API_URL}/quiz-sessions/stats/all-quizzes-summary`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // GET most difficult questions across all quizzes
  getMostDifficultQuestions: async (limit = 10) => {
    const response = await fetch(
      `${API_URL}/quiz-sessions/stats/difficult-questions?limit=${limit}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // GET most difficult questions for a specific quiz
  getQuizDifficultQuestions: async (quizId, limit = 5) => {
    const response = await fetch(
      `${API_URL}/quiz-sessions/quiz/${quizId}/difficult-questions?limit=${limit}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // GET question bank (professor)
  getQuestionBank: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/quizzes/questions${queryString ? `?${queryString}` : ''}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // GET aggregated topic performance
  getTopicPerformance: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/quiz-sessions/stats/topic-performance${queryString ? `?${queryString}` : ''}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // Legacy auth by email (kept for backwards compatibility)
  getUserByEmailForAuth: async email => {
    const response = await fetch(`${API_URL}/users/email/${email}`);
    return response.json();
  },

  getStudentsPerformance: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/users/students/performance${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  // GET difficulty mismatch analytics for all quizzes (Professor view)
  getDifficultyMismatches: async () => {
    const response = await fetch(`${API_URL}/quiz-sessions/difficulty-mismatches`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Organizations
  getOrganizations: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/organizations${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  getOrganization: async id => {
    const response = await fetch(`${API_URL}/organizations/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  createOrganization: async orgData => {
    const response = await fetch(`${API_URL}/organizations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(orgData),
    });
    return handleResponse(response);
  },

  updateOrganization: async (id, orgData) => {
    const response = await fetch(`${API_URL}/organizations/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(orgData),
    });
    return handleResponse(response);
  },

  deleteOrganization: async id => {
    const response = await fetch(`${API_URL}/organizations/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getOrganizationStats: async id => {
    const response = await fetch(`${API_URL}/organizations/${id}/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getOrganizationUsers: async (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/organizations/${id}/users${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  assignUsersToOrganization: async (id, userIds) => {
    const response = await fetch(`${API_URL}/organizations/${id}/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userIds }),
    });
    return handleResponse(response);
  },

  removeUsersFromOrganization: async (id, userIds) => {
    const response = await fetch(`${API_URL}/organizations/${id}/users`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userIds }),
    });
    return handleResponse(response);
  },

  // Courses
  getCourses: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/courses${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getOrganization: async id => {
    const response = await fetch(`${API_URL}/organizations/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getCourse: async id => {
    const response = await fetch(`${API_URL}/courses/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  createCourse: async courseData => {
    const response = await fetch(`${API_URL}/courses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(courseData),
    });
    return handleResponse(response);
  },

  createProfessorCourse: async courseData => {
    const response = await fetch(`${API_URL}/courses/professor`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(courseData),
    });
    return handleResponse(response);
  },

  updateProfessorCourse: async (id, courseData) => {
    const response = await fetch(`${API_URL}/courses/professor/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(courseData),
    });
    return handleResponse(response);
  },

  updateCourse: async (id, courseData) => {
    const response = await fetch(`${API_URL}/courses/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(courseData),
    });
    return handleResponse(response);
  },

  deleteCourse: async id => {
    const response = await fetch(`${API_URL}/courses/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  assignProfessorsToCourse: async (id, professorIds) => {
    const response = await fetch(`${API_URL}/courses/${id}/assign-professors`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ professorIds }),
    });
    return handleResponse(response);
  },

  removeProfessorsFromCourse: async (id, professorIds) => {
    const response = await fetch(`${API_URL}/courses/${id}/remove-professors`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ professorIds }),
    });
    return handleResponse(response);
  },

  getCoursesByOrganization: async (organizationId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/courses/organization/${organizationId}${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  // Enrollments
  requestEnrollment: async courseId => {
    const response = await fetch(`${API_URL}/enrollments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ courseId }),
    });
    return handleResponse(response);
  },

  getMyEnrollments: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/enrollments/my-enrollments${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  getAvailableCourses: async () => {
    const response = await fetch(`${API_URL}/enrollments/available-courses`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getEnrollmentRequests: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/enrollments/requests${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  reviewEnrollment: async (enrollmentId, status, rejectionReason = null) => {
    const response = await fetch(`${API_URL}/enrollments/${enrollmentId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status, rejectionReason }),
    });
    return handleResponse(response);
  },

  getEnrolledCourses: async () => {
    const response = await fetch(`${API_URL}/enrollments/enrolled-courses`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  cancelEnrollmentRequest: async enrollmentId => {
    const response = await fetch(`${API_URL}/enrollments/${enrollmentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getStudentEnrollments: async (studentId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/enrollments/student/${studentId}${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  getPlatformStats: async () => {
    const response = await fetch(`${API_URL}/platform/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getSystemHealth: async () => {
    const response = await fetch(`${API_URL}/health/status`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getAuditLogs: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/logs?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  deleteAuditLogs: async logIds => {
    const response = await fetch(`${API_URL}/logs`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ logIds }),
    });
    return handleResponse(response);
  },

  // Time Analytics
  getStudentTimeAnalytics: async studentEmail => {
    const response = await fetch(
      `${API_URL}/quiz-sessions/student-email/${studentEmail}/time-analytics`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  getQuestionBreakdown: async (studentEmail, quizId) => {
    const response = await fetch(
      `${API_URL}/quiz-sessions/student-email/${studentEmail}/quiz/${quizId}/question-breakdown`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  // Peer Comparison
  getStudentPeerComparison: async (studentEmail, courseId = null) => {
    const url = courseId
      ? `${API_URL}/quiz-sessions/student-email/${studentEmail}/peer-comparison?courseId=${courseId}`
      : `${API_URL}/quiz-sessions/student-email/${studentEmail}/peer-comparison`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Organization Logo Management
  uploadOrganizationLogo: async (organizationId, logoFile) => {
    const formData = new FormData();
    formData.append('logo', logoFile);

    const response = await fetch(`${API_URL}/organizations/${organizationId}/logo`, {
      method: 'POST',
      headers: {
        Authorization: getAuthHeaders().Authorization,
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      body: formData,
    });
    return handleResponse(response);
  },

  removeOrganizationLogo: async organizationId => {
    const response = await fetch(`${API_URL}/organizations/${organizationId}/logo`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Sample Data Generation
  generateSampleQuizzes: async (courseId, numberOfQuizzes, questionsPerQuiz, createdBy) => {
    const response = await fetch(`${API_URL}/sample-data/quizzes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ courseId, numberOfQuizzes, questionsPerQuiz, createdBy }),
    });
    return handleResponse(response);
  },

  deleteSampleQuizzes: async courseId => {
    const url = courseId
      ? `${API_URL}/sample-data/quizzes?courseId=${courseId}`
      : `${API_URL}/sample-data/quizzes`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getSampleDataStats: async () => {
    const response = await fetch(`${API_URL}/sample-data/stats`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  generateSampleCourses: async (organizationId, numberOfCourses, assignProfessors) => {
    const response = await fetch(`${API_URL}/sample-data/courses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ organizationId, numberOfCourses, assignProfessors }),
    });
    return handleResponse(response);
  },

  deleteSampleCourses: async organizationId => {
    const url = organizationId
      ? `${API_URL}/sample-data/courses?organizationId=${organizationId}`
      : `${API_URL}/sample-data/courses`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  generateSampleUsers: async (numberOfUsers, role, organizationId, password) => {
    const response = await fetch(`${API_URL}/sample-data/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ numberOfUsers, role, organizationId, password }),
    });
    return handleResponse(response);
  },

  deleteSampleUsers: async organizationId => {
    const url = organizationId
      ? `${API_URL}/sample-data/users?organizationId=${organizationId}`
      : `${API_URL}/sample-data/users`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  generateSampleQuizSessions: async (
    studentId,
    courseId,
    minTimePerQuestion,
    maxTimePerQuestion,
    accuracyRate
  ) => {
    const response = await fetch(`${API_URL}/sample-data/quiz-sessions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        studentId,
        courseId,
        minTimePerQuestion,
        maxTimePerQuestion,
        accuracyRate,
      }),
    });
    return handleResponse(response);
  },

  deleteSampleQuizSessions: async studentId => {
    const url = studentId
      ? `${API_URL}/sample-data/quiz-sessions?studentId=${studentId}`
      : `${API_URL}/sample-data/quiz-sessions`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // ====================================
  // RESOURCES
  // ====================================
  getResources: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/resources${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getResource: async id => {
    const response = await fetch(`${API_URL}/resources/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  uploadResourceFile: async formData => {
    const user = JSON.parse(localStorage.getItem('qualiz_user') || '{}');
    const token = user.token;

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/resources/upload`, {
      method: 'POST',
      headers: headers,
      body: formData, // Don't set Content-Type for FormData, let browser set it with boundary
    });
    return handleResponse(response);
  },

  createResourceUrl: async resourceData => {
    const response = await fetch(`${API_URL}/resources/url`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(resourceData),
    });
    return handleResponse(response);
  },

  createResourceText: async resourceData => {
    const response = await fetch(`${API_URL}/resources/text`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(resourceData),
    });
    return handleResponse(response);
  },

  updateResource: async (id, resourceData) => {
    const response = await fetch(`${API_URL}/resources/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(resourceData),
    });
    return handleResponse(response);
  },

  deleteResource: async id => {
    const response = await fetch(`${API_URL}/resources/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  trackResourceAccess: async id => {
    const response = await fetch(`${API_URL}/resources/${id}/download`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  markResourceHelpful: async id => {
    const response = await fetch(`${API_URL}/resources/${id}/helpful`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  unmarkResourceHelpful: async id => {
    const response = await fetch(`${API_URL}/resources/${id}/helpful`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  recommendResource: async (resourceId, payload) => {
    const response = await fetch(`${API_URL}/resources/${resourceId}/recommendations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  getResourceRecommendations: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/resources/recommendations${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  updateResourceRecommendation: async (recommendationId, payload) => {
    const response = await fetch(`${API_URL}/resources/recommendations/${recommendationId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  getSystemRecommendationsCount: async () => {
    const response = await fetch(`${API_URL}/resources/recommendations/system/count`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Get student topic performance (all topics with scores)
  getStudentTopicPerformance: async (email, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/quiz-sessions/student-email/${email}/topic-performance${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  // Get student weak topics based on quiz performance
  getStudentWeakTopics: async (email, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/quiz-sessions/student-email/${email}/weak-topics${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  // Get student course performance (for radar chart)
  getStudentCoursePerformance: async email => {
    const response = await fetch(
      `${API_URL}/quiz-sessions/student-email/${email}/course-performance`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  // Generate personalized quiz for a student
  generatePersonalizedQuiz: async (
    studentEmail,
    courseId,
    questionCount = 10,
    strategy = 'weakTopics',
    saveToDatabase = true
  ) => {
    const response = await fetch(`${API_URL}/quiz-sessions/generate-personalized-quiz`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        studentEmail,
        courseId,
        questionCount,
        strategy,
        saveToDatabase,
      }),
    });
    return handleResponse(response);
  },

  // Get personalized quiz history for a student
  getPersonalizedQuizHistory: async studentEmail => {
    const response = await fetch(`${API_URL}/quiz-sessions/personalized-quizzes/${studentEmail}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Delete a personalized quiz
  deletePersonalizedQuiz: async (quizId, studentEmail) => {
    const response = await fetch(`${API_URL}/quiz-sessions/personalized-quiz/${quizId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ studentEmail }),
    });
    return handleResponse(response);
  },

  // Messages
  sendMessage: async messageData => {
    const response = await fetch(`${API_URL}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(messageData),
    });
    return handleResponse(response);
  },

  getMessages: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/messages${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getInbox: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/messages/inbox${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  getSentMessages: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/messages/sent${queryString ? `?${queryString}` : ''}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  getMessage: async id => {
    const response = await fetch(`${API_URL}/messages/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  markMessageRead: async id => {
    const response = await fetch(`${API_URL}/messages/${id}/mark-read`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getUnreadMessageCount: async () => {
    const response = await fetch(`${API_URL}/messages/unread/count`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  sendBulkMessages: async bulkData => {
    const response = await fetch(`${API_URL}/messages/bulk`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(bulkData),
    });
    return handleResponse(response);
  },

  // Get enrolled students for a course
  getEnrolledStudents: async courseId => {
    const response = await fetch(`${API_URL}/enrollments/course/${courseId}/students`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // ====================================
  // ANALYTICS
  // ====================================
  generateCourseAnalysis: async analysisData => {
    const response = await fetch(`${API_URL}/analytics/generate-analysis`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(analysisData),
    });
    return handleResponse(response);
  },

  getCourseAnalyses: async (courseId, limit = 10, type = 'course', extraParams = {}) => {
    const params = new URLSearchParams({ limit: limit.toString(), type });

    // Add extra parameters if provided
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value != null) {
        params.append(key, value.toString());
      }
    });

    const response = await fetch(
      `${API_URL}/analytics/course/${courseId}/analyses?${params.toString()}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  getAnalysis: async (analysisId, type = 'course') => {
    const response = await fetch(`${API_URL}/analytics/${analysisId}?type=${type}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  deleteAnalysis: async (analysisId, type = 'course') => {
    const response = await fetch(`${API_URL}/analytics/${analysisId}?type=${type}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // System Settings
  getSystemSettings: async () => {
    const response = await fetch(`${API_URL}/system/settings`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateSystemSettings: async settings => {
    const response = await fetch(`${API_URL}/system/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings),
    });
    return handleResponse(response);
  },

  // ====================================
  // FEEDBACK
  // ====================================
  createFeedback: async feedbackData => {
    const user = JSON.parse(localStorage.getItem('qualiz_user') || '{}');
    const token = user.token;

    const formData = new FormData();
    formData.append('category', feedbackData.category);
    formData.append('description', feedbackData.description);

    if (feedbackData.severity) {
      formData.append('severity', feedbackData.severity);
    }

    if (feedbackData.pageUrl) {
      formData.append('pageUrl', feedbackData.pageUrl);
    }

    if (feedbackData.userRole) {
      formData.append('userRole', feedbackData.userRole);
    }

    if (feedbackData.browserInfo) {
      formData.append('browserInfo', JSON.stringify(feedbackData.browserInfo));
    }

    if (feedbackData.screenshot) {
      formData.append('screenshot', feedbackData.screenshot);
    }

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/feedback`, {
      method: 'POST',
      headers: headers,
      body: formData,
    });
    return handleResponse(response);
  },

  getFeedback: async (filters = {}) => {
    const queryString = new URLSearchParams(filters).toString();
    const response = await fetch(`${API_URL}/feedback${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getFeedbackById: async id => {
    const response = await fetch(`${API_URL}/feedback/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateFeedback: async (id, data) => {
    const response = await fetch(`${API_URL}/feedback/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deleteFeedback: async id => {
    const response = await fetch(`${API_URL}/feedback/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
};
